/** UI status for list/detail */
export type AnnouncementStatus =
  | "yayinda"
  | "kaldirildi"
  | "taslak"
  | "suresi_dolmus";

/** API status: 0 DRAFT, 1 PUBLISHED, 2 UNPUBLISHED */
export type AnnouncementStatusApi = 0 | 1 | 2;

/**
 * Duyurunun nerede gösterileceği (backend `AnnouncementDisplayType` aynası).
 *
 * HOMEPAGE: anasayfadaki duyuru alanında listelenir.
 * POPUP: uygulamaya girişte modal olarak açılır.
 */
export enum AnnouncementDisplayType {
  HOMEPAGE = 0,
  POPUP = 1,
}

export interface AnnouncementEntry {
  id: string;
  title: string;
  content: string;
  targetAudience: string;
  publishDate: string;
  status: AnnouncementStatus;
  /** Bitiş tarihi ISO — geçmişse status 'suresi_dolmus' olarak gösterilir */
  endDateIso?: string | null;
  imageUrl?: string;
  /** Backend image file ID (for FileImage / thumbnail) */
  imageFileID?: string | null;
}

export interface AnnouncementFilters {
  search?: string;
  status?: AnnouncementStatus | AnnouncementStatus[];
  targetAudience?: string | string[];
}

/** GET /api/admin/announcements - query params */
export interface AnnouncementListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: AnnouncementStatusApi;
  targetDepartmentIds?: string[];
  targetEducationIds?: string[];
  dateFrom?: string; // ISO 8601
  dateTo?: string;   // ISO 8601
  sortBy?: "createdAt" | "startDate" | "title" | "status";
  sortOrder?: "asc" | "desc";
}

/** GET /api/admin/announcements - single item from backend */
export interface AnnouncementListItemApi {
  id: string;
  title: string;
  description: string;
  imageFileID?: string | null;
  /** Yayın tarihi (API'den publishDate veya startDate olarak gelebilir) */
  publishDate?: string;
  startDate?: string;
  endDate?: string;
  actionUrl?: string;
  actionText?: string;
  targetAllStaff?: boolean;
  
  /** Hedef kitle - API'den gelen metin (örn. "Tüm Personel" veya birim adları) */
  targetAudience?: string | null;
  status: AnnouncementStatusApi;
  /** Gösterim türü; eski kayıtlarda gelmeyebilir, o durumda Anasayfa varsayılır. */
  displayType?: AnnouncementDisplayType;
  departments?: { id: string; name: string }[];
  createdAt?: string;
}

/** GET /api/admin/announcements - response wrapper */
export interface AnnouncementListResponseApi {
  success?: boolean;
  status?: number;
  data?: AnnouncementListItemApi[];
  count?: number;
  total?: number;
  message?: string;
}

/** POST /api/admin/announcements - request body (CreateAnnouncementDto) */
export interface CreateAnnouncementDto {
  /** *Required*, maxLength 500 */
  title: string;
  /** Optional, maxLength 10000 */
  description?: string;
  /** Optional, UUID from POST /admin/files/upload */
  imageFileID?: string | null;
  /** Optional, ISO 8601 */
  startDate?: string;
  /** Optional, ISO 8601 */
  endDate?: string;
  /** Optional, maxLength 2000 */
  actionUrl?: string;
  /** Optional, maxLength 500 */
  actionText?: string;
  /** *Required*. When true, targetDepartmentIds and targetEducationIds are ignored. */
  targetAllStaff: boolean;
  /** Optional; ignored when targetAllStaff is true */
  targetDepartmentIds?: string[];
  /** Optional; ignored when targetAllStaff is true */
  targetEducationIds?: string[];
  /** *Required*. 0 = Taslak (Draft), 1 = Yayınla (Published). Enum [0, 1] */
  status: 0 | 1;
  /** Optional; gönderilmezse backend Anasayfa (0) kabul eder. */
  displayType?: AnnouncementDisplayType;
}

/** PATCH /api/admin/announcements/{id} - request body (partial) */
export type UpdateAnnouncementDto = Partial<CreateAnnouncementDto>;

/** DELETE /api/admin/announcements/bulk - request body */
export interface BulkDeleteAnnouncementsDto {
  ids: string[];
}

export function apiStatusToUi(status: AnnouncementStatusApi): AnnouncementStatus {
  switch (status) {
    case 0: return "taslak";
    case 1: return "yayinda";
    case 2: return "kaldirildi";
    default: return "taslak";
  }
}

export function uiStatusToApi(status: AnnouncementStatus): AnnouncementStatusApi {
  switch (status) {
    case "taslak": return 0;
    case "yayinda": return 1;
    case "kaldirildi": return 2;
    default: return 0;
  }
}
